import React, { useState } from 'react'
import { Container, Title, Button, Stack, Table, Checkbox, Group, Select, ActionIcon, Tooltip, Modal, Text } from '@mantine/core'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useAppTranslation } from 'hooks/useTranslation'
import { notifications } from '@mantine/notifications'
import { IconRefresh, IconFilter, IconEdit, IconEye } from '@tabler/icons-react'

import { getCampaignsWithImpressions, bulkUpdateCampaignStatus, type CampaignWithImpressions } from 'supabase/campaigns'
import { targetingLabel } from 'supabase/campaigns/targeting'
import { CampaignStatusBadge, CampaignStatusActions } from 'components/campaigns'

type CampaignStatus = 'Draft' | 'Scheduled' | 'Active' | 'Paused' | 'Ended'

export function CampaignsPage() {
  const { t } = useAppTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([])
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'all'>('all')
  const [bulkStatusModal, setBulkStatusModal] = useState<{
    open: boolean
    newStatus: CampaignStatus | null
  }>({ open: false, newStatus: null })

  const { data: campaignsData, isLoading, refetch } = useQuery({
    queryKey: ['campaigns-with-impressions', statusFilter],
    queryFn: async () => {
      const data = await getCampaignsWithImpressions()
      if (statusFilter === 'all') {
        return data
      }
      return data?.filter((c: CampaignWithImpressions) => c.status === statusFilter)
    },
    refetchInterval: 60000 // Auto-refresh every 60 seconds
  })

  const campaigns = campaignsData || []

  const bulkStatusMutation = useMutation({
    mutationFn: async (newStatus: CampaignStatus) => {
      return bulkUpdateCampaignStatus(selectedCampaigns, { status: newStatus })
    },
    onSuccess: (result, newStatus) => {
      const successCount = result.success.length
      const failedCount = result.failed.length

      if (successCount > 0) {
        notifications.show({
          title: t('campaigns.bulk.success'),
          message: t('campaigns.bulk.updated', { count: successCount, status: newStatus }),
          color: 'green'
        })
      }

      if (failedCount > 0) {
        notifications.show({
          title: t('campaigns.bulk.partialSuccess'),
          message: t('campaigns.bulk.failed', { count: failedCount }),
          color: 'yellow'
        })
      }

      queryClient.invalidateQueries({ queryKey: ['campaigns-with-impressions'] })
      setSelectedCampaigns([])
    },
    onError: () => {
      notifications.show({
        title: t('campaigns.bulk.error'),
        message: t('campaigns.bulk.errorMessage'),
        color: 'red'
      })
    }
  })

  const handleSelectAll = () => {
    if (selectedCampaigns.length === campaigns.length) {
      setSelectedCampaigns([])
    } else {
      setSelectedCampaigns(campaigns.map((c: CampaignWithImpressions) => c.id))
    }
  }

  const handleSelectCampaign = (campaignId: string) => {
    setSelectedCampaigns(prev =>
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    )
  }

  const handleBulkStatusChange = (newStatus: CampaignStatus) => {
    setBulkStatusModal({ open: true, newStatus })
  }

  const confirmBulkStatusChange = () => {
    if (bulkStatusModal.newStatus) {
      bulkStatusMutation.mutate(bulkStatusModal.newStatus)
    }
    setBulkStatusModal({ open: false, newStatus: null })
  }

  const availableBulkActions: CampaignStatus[] = ['Active', 'Paused', 'Ended']

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <Container size='xl' py='xl'>
      <Stack gap='lg'>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title order={1}>{t('campaigns.title')}</Title>
          <Group>
            <Select
              placeholder={t('campaigns.filter.status')}
              data={[
                { value: 'all', label: t('campaigns.filter.all') },
                { value: 'Draft', label: t('campaigns.status_draft') },
                { value: 'Scheduled', label: t('campaigns.status_scheduled') },
                { value: 'Active', label: t('campaigns.status_active') },
                { value: 'Paused', label: t('campaigns.status_paused') },
                { value: 'Ended', label: t('campaigns.status_ended') }
              ]}
              value={statusFilter}
              onChange={(value) => setStatusFilter((value || 'all') as CampaignStatus | 'all')}
              leftSection={<IconFilter size={16} />}
              clearable
            />
            <Tooltip label={t('campaigns.refresh')}>
              <ActionIcon onClick={() => refetch()} variant='default'>
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
            <Button component={Link} to='/campaigns/create'>
              {t('campaigns.create_campaign')}
            </Button>
          </Group>
        </div>

        {selectedCampaigns.length > 0 && (
          <Group>
            <Text size='sm'>
              {t('campaigns.bulk.selected', { count: selectedCampaigns.length })}
            </Text>
            <Group gap='xs'>
              {availableBulkActions.map(status => (
                <Button
                  key={status}
                  size='xs'
                  variant='outline'
                  onClick={() => handleBulkStatusChange(status)}
                >
                  {t(`campaigns.bulk.changeTo${status}`)}
                </Button>
              ))}
            </Group>
          </Group>
        )}

        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Checkbox
                  checked={campaigns.length > 0 && selectedCampaigns.length === campaigns.length}
                  indeterminate={selectedCampaigns.length > 0 && selectedCampaigns.length < campaigns.length}
                  onChange={handleSelectAll}
                />
              </Table.Th>
              <Table.Th>{t('campaigns.name')}</Table.Th>
              <Table.Th>{t('campaigns.advertiser')}</Table.Th>
              <Table.Th>{t('campaigns.tier')}</Table.Th>
              <Table.Th>{t('campaigns.language_target')}</Table.Th>
              <Table.Th>{t('campaigns.platform_target')}</Table.Th>
              <Table.Th>{t('campaigns.start_date')}</Table.Th>
              <Table.Th>{t('campaigns.end_date')}</Table.Th>
              <Table.Th>{t('campaigns.status_column')}</Table.Th>
              <Table.Th>{t('campaigns.impressions')}</Table.Th>
              <Table.Th>{t('campaigns.actions')}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {campaigns.map((campaign: CampaignWithImpressions) => (
              <Table.Tr
                key={campaign.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/campaigns/${campaign.id}`)}
              >
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedCampaigns.includes(campaign.id)}
                    onChange={() => handleSelectCampaign(campaign.id)}
                  />
                </Table.Td>
                <Table.Td>{campaign.name}</Table.Td>
                <Table.Td>{campaign.advertisers?.name || 'N/A'}</Table.Td>
                <Table.Td>{campaign.tier}</Table.Td>
                <Table.Td>{targetingLabel('language_target', campaign.language_target, t)}</Table.Td>
                <Table.Td>{targetingLabel('platform_target', campaign.platform_target, t)}</Table.Td>
                <Table.Td>{new Date(campaign.start_date).toLocaleDateString()}</Table.Td>
                <Table.Td>{new Date(campaign.end_date).toLocaleDateString()}</Table.Td>
                <Table.Td>
                  <CampaignStatusBadge status={campaign.status as CampaignStatus} />
                </Table.Td>
                <Table.Td>{campaign.total_impressions.toLocaleString()}</Table.Td>
                <Table.Td onClick={(e) => e.stopPropagation()}>
                  <Group gap="xs">
                    <Tooltip label={t('common.view')}>
                      <ActionIcon
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/campaigns/${campaign.id}`)
                        }}
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label={t('common.edit')}>
                      <ActionIcon
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/campaigns/${campaign.id}/edit`)
                        }}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                    </Tooltip>
                    <CampaignStatusActions campaign={campaign} />
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {campaigns.length === 0 && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>{t('campaigns.empty')}</p>
            <Button component={Link} to='/campaigns/create'>
              {t('campaigns.createFirst')}
            </Button>
          </div>
        )}
      </Stack>

      <Modal
        opened={bulkStatusModal.open}
        onClose={() => setBulkStatusModal({ open: false, newStatus: null })}
        title={t('campaigns.bulk.confirm')}
      >
        <Stack>
          <Text>
            {t('campaigns.bulk.confirmMessage', {
              count: selectedCampaigns.length,
              status: bulkStatusModal.newStatus
            })}
          </Text>
          <Group justify='flex-end'>
            <Button
              variant='default'
              onClick={() => setBulkStatusModal({ open: false, newStatus: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color='blue'
              onClick={confirmBulkStatusChange}
              loading={bulkStatusMutation.isPending}
            >
              {t('common.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}